import React, { useState, useEffect, useMemo } from 'react';

// ─── Font constants ───────────────────────────────────────────────────────────
const sans = "'DM Sans', system-ui, sans-serif";
const serif = "'DM Serif Display', Georgia, serif";

// ─── Pattern Calendar — free tier ───────────────────────────────────────────
// Month-view grid. Each logged day is coloured by intensity (symptom count +
// average severity). Tap a day to see everything logged that day, including
// the user's own note. Notes are shown to the user only — they are never
// passed to any AI prompt.
//
// Data comes from /api/get-trends (calendarLogs — 90 day window).

const SYMPTOM_LABELS = {
  hot_flashes: 'Hot flashes',
  night_sweats: 'Night sweats',
  sleep_issues: 'Sleep issues',
  fatigue: 'Fatigue',
  brain_fog: 'Brain fog',
  mood_irritability: 'Mood swings',
  anxiety: 'Anxiety',
  low_mood: 'Low mood',
  joint_pain: 'Joint pain',
  headaches: 'Headaches',
  heart_palpitations: 'Heart palpitations',
  dizziness: 'Dizziness',
  bloating: 'Bloating',
  weight_changes: 'Weight changes',
  breast_tenderness: 'Breast tenderness',
  skin_changes: 'Skin changes',
  hair_thinning: 'Hair thinning',
  vaginal_dryness: 'Vaginal dryness',
  frequent_urination: 'Frequent urination',
  recurrent_utis: 'Recurrent UTIs',
  low_libido: 'Low libido',
  pain_during_sex: 'Pain during sex',
  sensitivity_changes: 'Sensitivity changes',
  period_changes: 'Period changes',
};

const CYCLE_LABELS = {
  period_light: 'Light period',
  period_heavy: 'Heavy period',
  spotting: 'Spotting',
  no_bleeding: 'No bleeding',
  irregular: 'Irregular',
  no_periods: 'No periods',
};

const LIFESTYLE_META = {
  alcohol: { label: 'Alcohol' },
  poor_sleep: { label: 'Poor sleep' },
  high_stress: { label: 'High stress' },
  exercise: { label: 'Exercised' },
  caffeine: { label: 'Caffeine' },
  travel: { label: 'Travel' },
};

// Intensity fills — 0 (logged, calm day) → 3 (hard day)
const INTENSITY_FILLS = ['#F7EDE9', '#EFD4CE', '#E2B4AE', '#D4A5A5'];

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

// Day score: number of symptoms + how severe they were.
// 0 symptoms = 0 · light day = 1 · moderate = 2 · heavy = 3
function dayIntensity(log) {
  const symptoms = log?.symptoms || {};
  const keys = Object.keys(symptoms);
  if (keys.length === 0) return 0;
  const avgSeverity = keys.reduce((sum, k) => sum + (symptoms[k] || 0), 0) / keys.length;
  const score = keys.length * avgSeverity; // e.g. 3 symptoms at sev 3 = 9
  if (score <= 4) return 1;
  if (score <= 10) return 2;
  return 3;
}

function toDateStr(d) {
  return d.toISOString().split('T')[0];
}

function SymptomCalendar({ token, onJoinWaitlist }) {
  const [calendarLogs, setCalendarLogs] = useState(null);
  const [calendarFrom, setCalendarFrom] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [viewYear, setViewYear] = useState(new Date().getFullYear());
  const [viewMonth, setViewMonth] = useState(new Date().getMonth());
  const [selectedDate, setSelectedDate] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/api/get-trends', {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error('fetch failed');
        const data = await res.json();
        if (cancelled) return;
        setCalendarLogs(data.calendarLogs || []);
        setCalendarFrom(data.calendarFrom || null);
      } catch (err) {
        if (!cancelled) setLoadError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const logsByDate = useMemo(() => {
    const map = {};
    (calendarLogs || []).forEach(l => { map[l.log_date] = l; });
    return map;
  }, [calendarLogs]);

  const today = new Date();
  const todayStr = toDateStr(today);

  // Month grid: weeks start Monday
  const grid = useMemo(() => {
    const firstOfMonth = new Date(viewYear, viewMonth, 1);
    const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
    // JS getDay(): 0=Sun..6=Sat → convert so 0=Mon..6=Sun
    const leadingBlanks = (firstOfMonth.getDay() + 6) % 7;
    const cells = [];
    for (let i = 0; i < leadingBlanks; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) {
      cells.push(new Date(viewYear, viewMonth, d));
    }
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }, [viewYear, viewMonth]);

  const canGoPrev = useMemo(() => {
    if (!calendarFrom) return false;
    const prevMonthEnd = new Date(viewYear, viewMonth, 0); // last day of prev month
    return toDateStr(prevMonthEnd) >= calendarFrom;
  }, [viewYear, viewMonth, calendarFrom]);

  const canGoNext =
    viewYear < today.getFullYear() ||
    (viewYear === today.getFullYear() && viewMonth < today.getMonth());

  const goPrev = () => {
    setSelectedDate(null);
    if (viewMonth === 0) { setViewYear(viewYear - 1); setViewMonth(11); }
    else setViewMonth(viewMonth - 1);
  };
  const goNext = () => {
    setSelectedDate(null);
    if (viewMonth === 11) { setViewYear(viewYear + 1); setViewMonth(0); }
    else setViewMonth(viewMonth + 1);
  };

  const selectedLog = selectedDate ? logsByDate[selectedDate] : null;

  // Month stats for the little summary line
  const monthStats = useMemo(() => {
    let logged = 0, hard = 0;
    grid.forEach(d => {
      if (!d) return;
      const log = logsByDate[toDateStr(d)];
      if (log) {
        logged++;
        if (dayIntensity(log) === 3) hard++;
      }
    });
    return { logged, hard };
  }, [grid, logsByDate]);

  if (loading) {
    return (
      <div style={styles.loadingWrap}>
        <div style={{ display: 'inline-flex', gap: '8px', alignItems: 'center' }}>
          <div style={{ ...styles.loadingDot }} />
          <div style={{ ...styles.loadingDot, animationDelay: '0.15s' }} />
          <div style={{ ...styles.loadingDot, animationDelay: '0.3s' }} />
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div style={styles.emptyWrap}>
        <p style={styles.emptyText}>Couldn't load your calendar. Please try again.</p>
      </div>
    );
  }

  if (!calendarLogs || calendarLogs.length === 0) {
    return (
      <div style={styles.emptyWrap}>
        <div style={styles.emptyIcon}>0</div>
        <p style={styles.emptyTitle}>Your calendar starts with your first log</p>
        <p style={styles.emptyText}>
          Once you start logging, this becomes a month-by-month picture of your
          patterns — the good days, the hard days, and what's driving them.
        </p>
      </div>
    );
  }

  return (
    <div style={styles.wrapper}>

      {/* Month navigation */}
      <div style={styles.monthNav}>
        <button
          style={{ ...styles.navBtn, ...(canGoPrev ? {} : styles.navBtnDisabled) }}
          onClick={goPrev}
          disabled={!canGoPrev}
        >
          ←
        </button>
        <div style={styles.monthTitle}>{MONTH_NAMES[viewMonth]} {viewYear}</div>
        <button
          style={{ ...styles.navBtn, ...(canGoNext ? {} : styles.navBtnDisabled) }}
          onClick={goNext}
          disabled={!canGoNext}
        >
          →
        </button>
      </div>

      <p style={styles.monthSummary}>
        {monthStats.logged === 0
          ? 'No logs this month yet'
          : `${monthStats.logged} day${monthStats.logged !== 1 ? 's' : ''} logged${monthStats.hard > 0 ? ` · ${monthStats.hard} hard day${monthStats.hard !== 1 ? 's' : ''}` : ''}`}
      </p>

      {/* Weekday headers */}
      <div style={styles.weekRow}>
        {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((d, i) => (
          <div key={i} style={styles.weekday}>{d}</div>
        ))}
      </div>

      {/* Day grid */}
      <div style={styles.dayGrid}>
        {grid.map((date, i) => {
          if (!date) return <div key={i} style={styles.dayBlank} />;
          const dateStr = toDateStr(date);
          const log = logsByDate[dateStr];
          const isFuture = dateStr > todayStr;
          const isToday = dateStr === todayStr;
          const isSelected = dateStr === selectedDate;
          const intensity = log ? dayIntensity(log) : null;

          return (
            <button
              key={i}
              style={{
                ...styles.dayCell,
                ...(log ? { backgroundColor: INTENSITY_FILLS[intensity], borderColor: 'transparent' } : {}),
                ...(isFuture ? styles.dayFuture : {}),
                ...(isToday ? styles.dayToday : {}),
                ...(isSelected ? styles.daySelected : {}),
              }}
              onClick={() => log && setSelectedDate(isSelected ? null : dateStr)}
              disabled={!log}
            >
              <span style={{
                ...styles.dayNum,
                ...(intensity === 3 ? { color: '#7C4A4A' } : {}),
              }}>
                {date.getDate()}
              </span>
            </button>
          );
        })}
      </div>

      {/* Legend */}
      <div style={styles.legend}>
        <span style={styles.legendLabel}>Calmer</span>
        {INTENSITY_FILLS.map((fill, i) => (
          <div key={i} style={{ ...styles.legendSwatch, backgroundColor: fill }} />
        ))}
        <span style={styles.legendLabel}>Harder</span>
      </div>

      {/* Day detail */}
      {selectedLog && (
        <div style={styles.detail}>
          <div style={styles.detailHeader}>
            <span style={styles.detailDate}>
              {new Date(selectedDate).toLocaleDateString('en-GB', {
                weekday: 'long', day: 'numeric', month: 'long',
              })}
            </span>
            <button style={styles.detailClose} onClick={() => setSelectedDate(null)}>×</button>
          </div>

          {selectedLog.symptoms && Object.keys(selectedLog.symptoms).length > 0 ? (
            <div style={styles.detailSection}>
              <div style={styles.detailLabel}>Symptoms</div>
              <div style={styles.symptomChips}>
                {Object.entries(selectedLog.symptoms).map(([key, sev]) => (
                  <div key={key} style={styles.symptomChip}>
                    <span style={styles.symptomChipName}>{SYMPTOM_LABELS[key] || key}</span>
                    <span style={styles.symptomChipSev}>{sev}/5</span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div style={styles.detailSection}>
              <div style={styles.detailLabel}>Symptoms</div>
              <p style={styles.detailNone}>A calm day — no symptoms logged</p>
            </div>
          )}

          {selectedLog.daily_scores && (
            <div style={styles.detailSection}>
              <div style={styles.detailLabel}>Scores</div>
              <div style={styles.scoreRow}>
                {['mood', 'energy', 'sleep', 'stress'].map(key => (
                  selectedLog.daily_scores[key] != null && (
                    <div key={key} style={styles.scorePill}>
                      <span style={styles.scorePillLabel}>{key.charAt(0).toUpperCase() + key.slice(1)}</span>
                      <span style={styles.scorePillValue}>{selectedLog.daily_scores[key]}/10</span>
                    </div>
                  )
                ))}
              </div>
            </div>
          )}

          {selectedLog.cycle_phase && (
            <div style={styles.detailSection}>
              <div style={styles.detailLabel}>Cycle</div>
              <p style={styles.detailText}>{CYCLE_LABELS[selectedLog.cycle_phase] || selectedLog.cycle_phase}</p>
            </div>
          )}

          {Array.isArray(selectedLog.lifestyle_tags) && selectedLog.lifestyle_tags.length > 0 && (
            <div style={styles.detailSection}>
              <div style={styles.detailLabel}>Lifestyle</div>
              <div style={styles.lifestyleRow}>
                {selectedLog.lifestyle_tags.map(tag => {
                  const meta = LIFESTYLE_META[tag];
                  if (!meta) return null;
                  return (
                    <span key={tag} style={styles.lifestyleTag}>
                      {meta.label}
                    </span>
                  );
                })}
              </div>
            </div>
          )}

          {selectedLog.notes && (
            <div style={styles.detailSection}>
              <div style={styles.detailLabel}>Your note</div>
              <p style={styles.noteText}>"{selectedLog.notes}"</p>
            </div>
          )}
        </div>
      )}

      {/* Pro hook */}
      <div style={styles.proHint}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
          <span style={styles.proHintBadge}>Pro</span>
        </div>
        <p style={styles.proHintText}>
          See what's driving your hard days — cycle patterns, lifestyle triggers, symptom chains
        </p>
        <button style={styles.proHintBtn} onClick={() => onJoinWaitlist && onJoinWaitlist('pattern_analysis')}>
          Unlock with Pro →
        </button>
      </div>

    </div>
  );
}

const styles = {
  wrapper: { paddingBottom: '24px' },

  loadingWrap: { padding: '60px 0', textAlign: 'center' },
  loadingDot: {
    width: '10px', height: '10px', borderRadius: '50%',
    backgroundColor: '#D4A5A5', animation: 'blink 1.2s infinite ease-in-out',
  },

  emptyWrap: { textAlign: 'center', padding: '48px 24px' },
  emptyIcon: {
    fontFamily: serif, fontSize: '48px', fontWeight: '400',
    color: '#D4A5A5', marginBottom: '16px', lineHeight: 1,
  },
  emptyTitle: { fontFamily: serif, fontSize: '22px', fontWeight: '400', color: '#1a1a18', marginBottom: '10px' },
  emptyText: { fontFamily: sans, fontSize: '14px', color: '#4a6070', lineHeight: '1.65', maxWidth: '340px', margin: '0 auto', fontWeight: '300' },

  monthNav: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    marginBottom: '4px',
  },
  navBtn: {
    background: 'white', border: '1px solid rgba(44,44,42,0.12)', borderRadius: '10px',
    width: '38px', height: '38px', fontSize: '16px', color: '#2C2C2A',
    cursor: 'pointer', fontFamily: sans,
  },
  navBtnDisabled: { opacity: 0.3, cursor: 'not-allowed' },
  monthTitle: { fontFamily: serif, fontSize: '20px', fontWeight: '400', color: '#1a1a18' },
  monthSummary: {
    fontFamily: sans, fontSize: '13px', color: '#6c8396', textAlign: 'center', margin: '2px 0 16px', fontWeight: '300',
  },

  weekRow: {
    display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '6px',
    marginBottom: '6px',
  },
  weekday: {
    fontFamily: sans, fontSize: '11px', fontWeight: '600', color: '#b4b2a9',
    textAlign: 'center', textTransform: 'uppercase', letterSpacing: '0.06em',
  },

  dayGrid: {
    display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '6px',
  },
  dayBlank: { aspectRatio: '1' },
  dayCell: {
    aspectRatio: '1', borderRadius: '10px',
    background: 'white', border: '1px solid rgba(44,44,42,0.08)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    cursor: 'pointer', fontFamily: sans, padding: 0,
  },
  dayFuture: { opacity: 0.3, cursor: 'default' },
  dayToday: { boxShadow: '0 0 0 2px #7A9E87' },
  daySelected: { boxShadow: '0 0 0 2px #D4A5A5' },
  dayNum: { fontFamily: sans, fontSize: '13px', fontWeight: '400', color: '#5F5E5A' },

  legend: {
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    gap: '6px', marginTop: '16px',
  },
  legendSwatch: { width: '16px', height: '16px', borderRadius: '5px' },
  legendLabel: { fontFamily: sans, fontSize: '11px', color: '#b4b2a9', margin: '0 4px', fontWeight: '300' },

  detail: {
    marginTop: '20px', backgroundColor: 'white',
    border: '1px solid rgba(44,44,42,0.08)', borderRadius: '14px', padding: '18px 20px',
  },
  detailHeader: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    marginBottom: '14px',
  },
  detailDate: { fontFamily: serif, fontSize: '16px', fontWeight: '400', color: '#1a1a18' },
  detailClose: {
    background: 'none', border: 'none', fontSize: '22px', color: '#b4b2a9',
    cursor: 'pointer', lineHeight: 1, padding: '2px 6px',
  },
  detailSection: { marginBottom: '14px' },
  detailLabel: {
    fontFamily: sans, fontSize: '11px', fontWeight: '600', color: '#b4b2a9',
    textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '8px',
  },
  detailNone: { fontFamily: sans, fontSize: '14px', color: '#6c8396', margin: 0, fontWeight: '300' },
  detailText: { fontFamily: sans, fontSize: '14px', color: '#2C2C2A', margin: 0 },

  symptomChips: { display: 'flex', flexWrap: 'wrap', gap: '8px' },
  symptomChip: {
    display: 'flex', alignItems: 'center', gap: '8px',
    backgroundColor: '#FAF7F2', borderRadius: '100px', padding: '6px 12px',
    border: '1px solid rgba(44,44,42,0.06)',
  },
  symptomChipName: { fontFamily: sans, fontSize: '13px', fontWeight: '500', color: '#2C2C2A' },
  symptomChipSev: { fontFamily: serif, fontSize: '13px', color: '#D4A5A5', fontWeight: '400' },

  scoreRow: { display: 'flex', flexWrap: 'wrap', gap: '8px' },
  scorePill: {
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    backgroundColor: '#FAF7F2', borderRadius: '10px', padding: '8px 14px',
    border: '1px solid rgba(44,44,42,0.06)',
  },
  scorePillLabel: { fontFamily: sans, fontSize: '11px', color: '#6c8396', fontWeight: '400' },
  scorePillValue: { fontFamily: serif, fontSize: '16px', fontWeight: '400', color: '#1a1a18' },

  lifestyleRow: { display: 'flex', flexWrap: 'wrap', gap: '8px' },
  lifestyleTag: {
    fontFamily: sans, fontSize: '13px', fontWeight: '400', color: '#4a6070',
    backgroundColor: '#FAF7F2', borderRadius: '100px', padding: '6px 12px',
    border: '1px solid rgba(44,44,42,0.06)',
  },

  noteText: {
    fontFamily: serif, fontSize: '15px', color: '#4a6070', fontStyle: 'italic',
    lineHeight: '1.65', margin: 0,
  },

  proHint: {
    backgroundColor: 'white', border: '1px solid rgba(212,165,165,0.25)',
    borderLeft: '3px solid #D4A5A5', borderRadius: '10px',
    padding: '16px 20px', marginTop: '20px',
  },
  proHintBadge: {
    display: 'inline-block', color: '#D4A5A5', fontFamily: sans,
    fontSize: '10px', fontWeight: '600', padding: '3px 10px',
    border: '1px solid rgba(212,165,165,0.4)',
    borderRadius: '100px', letterSpacing: '0.08em',
  },
  proHintText: { fontFamily: sans, fontSize: '13px', color: '#4a6070', lineHeight: '1.55', margin: '8px 0 12px', fontWeight: '300' },
  proHintBtn: {
    backgroundColor: '#2C2C2A', color: 'white', border: 'none',
    borderRadius: '10px', padding: '10px 20px',
    fontSize: '13px', fontWeight: '600', cursor: 'pointer',
    fontFamily: sans,
  },
};

export default SymptomCalendar;
